import { useState, type FC } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Crosshair
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import type { CasePrepUscisNeedsInputItem } from "../types";
import { stringifyValue } from "../utils";

type Props = {
  items: CasePrepUscisNeedsInputItem[];
  /** The form's values as edited, unsaved typing included. */
  values: Record<string, unknown>;
  onChange: (fieldName: string, value: string) => void;
  onLocate: (fieldName: string) => void;
};

/** Whether the attorney's current value settles the item. */
export const isResolved = (
  item: CasePrepUscisNeedsInputItem,
  value: unknown
): boolean => {
  const text = stringifyValue(value);
  if (item.reason === "conflicts") {
    return (
      text.trim().toLowerCase() === (item.expected ?? "").trim().toLowerCase()
    );
  }
  if (item.reason === "too_long") {
    return Boolean(item.max_length) && text.length <= (item.max_length ?? 0);
  }
  return text.trim().length > 0;
};

/**
 * What the fill could not settle, answered in place: values too long for their
 * printed box and essential fields left empty. Edits go into the form's unsaved
 * values, so Save Changes commits them with everything else.
 */
export const NeedsInputPanel: FC<Props> = ({
  items,
  values,
  onChange,
  onLocate
}) => {
  const [open, setOpen] = useState(true);
  if (items.length === 0) return null;
  const openCount = items.filter(
    (item) => !isResolved(item, values[item.field_name])
  ).length;

  return (
    <section
      aria-label="Needs input"
      className="flex-none border-b border-border bg-warn-soft"
      data-testid="form-needs-input"
    >
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm font-medium text-foreground"
      >
        {openCount > 0 ? (
          <AlertTriangle className="size-4 text-warn" aria-hidden />
        ) : (
          <CheckCircle2 className="size-4 text-success" aria-hidden />
        )}
        <span>Needs input</span>
        <span className="text-muted-foreground">
          {openCount > 0
            ? `${openCount} of ${items.length} open`
            : "All answered — save to keep them"}
        </span>
        <ChevronDown
          className={cn(
            "ml-auto size-4 text-muted-foreground transition-transform",
            open ? "rotate-180" : ""
          )}
          aria-hidden
        />
      </button>
      {open ? (
        <ul className="flex max-h-56 flex-col gap-2 overflow-y-auto px-4 pb-3">
          {items.map((item) => {
            const value = stringifyValue(values[item.field_name]);
            const resolved = isResolved(item, values[item.field_name]);
            const tooLong = item.reason === "too_long";
            const inputId = `needs-input-${item.field_name}`;
            return (
              <li
                key={item.field_name}
                className="flex flex-col gap-1 rounded-md border border-border bg-background px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  <label
                    htmlFor={inputId}
                    className="min-w-0 flex-1 wrap-break-word text-sm font-medium text-foreground"
                  >
                    {item.label}
                  </label>
                  {typeof item.page_index === "number" ? (
                    <button
                      type="button"
                      onClick={() => onLocate(item.field_name)}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
                      aria-label={`Show ${item.label} on the form`}
                    >
                      <Crosshair className="size-3" aria-hidden />
                      Page {item.page_index + 1}
                    </button>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    id={inputId}
                    value={value}
                    onChange={(event) =>
                      onChange(item.field_name, event.target.value)
                    }
                    maxLength={
                      tooLong ? undefined : item.max_length ?? undefined
                    }
                    aria-invalid={!resolved}
                    placeholder={tooLong ? undefined : "Type the answer"}
                    className="h-7 text-sm"
                  />
                  {item.reason === "conflicts" && !resolved ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 shrink-0"
                      onClick={() =>
                        onChange(item.field_name, item.expected ?? "")
                      }
                    >
                      {item.expected ? `Use ${item.expected}` : "Clear"}
                    </Button>
                  ) : null}
                  {tooLong && item.max_length ? (
                    <span
                      className={cn(
                        "shrink-0 text-xs tabular-nums",
                        resolved ? "text-success" : "text-destructive"
                      )}
                      data-testid={`needs-input-count-${item.field_name}`}
                    >
                      {value.length} / {item.max_length}
                    </span>
                  ) : null}
                </div>
                <span className="text-xs text-muted-foreground">
                  {resolved
                    ? "Answered"
                    : tooLong
                    ? `Too long to print: ${item.detail}. Shorten it, or move the rest to the additional information part.`
                    : item.detail}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
};
